import { TooltipTrigger } from '../../../../Provider/TooltipManager.js'
import { handleCSVUpload } from '../FileHandling/CSVHelper.js'
import { DocumentArrowDownIcon, DocumentArrowUpIcon, PencilSquareIcon } from '@heroicons/react/24/outline/index.js'
import { downloadSamplePoolCSV } from '../../HomePage/Modals/CSVDownload.js'
import { planCsvImport } from '../../HomePage/Modals/PlanCsvImport.ts'
import { hasStructureChanges } from '../../HomePage/Modals/SheetStructurePlan.ts'
import { mapToBatches } from '../../HomePage/Modals/MapToBatches.js'
import Actions from '../../../../Flux/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import { type CSVMode, stringifyError, stringifyWarning } from '../../HomePage/Modals/CSVModalTypes.js'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../../Model/Message/IMessage.js'
import { useEffect, useState } from 'react'
import { getSampleMetaDatas } from '../../../../Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import { getSamplePools } from '../../../../Flux/Selector/Order/GetSamplePool/GetSamplePoolSelector.js'
import { useDispatch, useStore } from 'react-redux'
import type { State } from '../../../../Flux/Slice/index.js'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector/index.js'
import type { IOrder, ISamplePool } from '../../../../Model/index.js'
import { canUseCsvGridEditor } from '../../../../Model/index.js'
import { useConfirm } from '../../../Component/ConfirmProvider.js'
import type { ParseCSVModalResult } from '../../HomePage/Modals/ParseCSVModal.js'
import { useModal } from '../../../Modal/UseModal.js'
import { buildSamplePoolCsvTable } from '../../HomePage/Modals/PoolCsvTable.ts'
import { trackOrderAction } from '../../../../Services/Telemetry/index.ts'

interface OrderDetailActionBarProps {
  order: IOrder
  samplePools: ISamplePool[] | 'LOADING'
}

export const OrderDetailActionBar = (props: OrderDetailActionBarProps) => {
  const [csvMode, setCsvMode] = useState<CSVMode>('PREPARE_METADATA')
  const store = useStore<typeof State>()
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const hasSamples = props.samplePools !== 'LOADING' && props.samplePools.some((x) => x.negativeControls.length + x.samples.length > 0)
  const { confirm } = useConfirm()
  const { openModal } = useModal()
  const canEditCsvInGrid = canUseCsvGridEditor(user)
  useEffect(() => {
    if (!props.order.lab) {
      setCsvMode('PREPARE_METADATA')
    } else if (!props.order.orderSubmission) {
      setCsvMode('CREATE_SAMPLES')
    } else {
      setCsvMode('UPDATE_METADATA')
    }
  }, [props.order])

  const canEditCsv = hasSamples || props.order.lab !== user?.org?.id

  /* Runs when the import modal closes, possibly after other saves: read the store now, not the props of
     the render that opened the modal, or metadata saved in between gets created a second time. */
  const csvOk = (result: ParseCSVModalResult) => {
    const samplePools = getSamplePools(store.getState())
    if (samplePools === 'LOADING') {
      throw Error('state should not be able to toggle back to loading')
    }
    try {
      const { ops, structure, assessment } = planCsvImport(
        samplePools,
        getSampleMetaDatas(store.getState()),
        result.data,
        result.flags,
        csvMode,
        props.order,
        result.sheet
      )

      if (ops.errors.length <= 0) {
        const handler = () => {
          if (structure && hasStructureChanges(structure)) {
            dispatch(Actions[orderSlice.name].applySheetImportRequest({ orderId: props.order.id, plan: structure, ops: ops.result }))
            return
          }
          const batches = mapToBatches(ops)
          batches.forEach((batch) => dispatch(Actions[orderSlice.name].batchUpdateSamples(batch)))
        }

        if (ops.warnings.length <= 0 && !assessment?.confirm) {
          handler()
        } else {
          confirm(
            {
              title: assessment?.confirm ? 'Check the sample types and pools' : 'Warnings where encountered while processing the CSV',
              message: (
                <>
                  {assessment?.hasChanges && <p className="mb-2">This import {assessment.summary}.</p>}
                  {assessment?.confirm && (
                    <ul className="list-disc mb-2">
                      {assessment.reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  <p>Do you want to apply these changes?</p>
                  <ul className="list-disc">
                    {ops.warnings.map((w, index) => (
                      <li key={index}>{stringifyWarning(w)}</li>
                    ))}
                  </ul>
                </>
              )
            },
            handler
          ).then(() => console.log('fulfilled promise from confirm'))
        }
      } else {
        console.error(ops.errors)
        ops.errors.forEach((x) => dispatch(addMessage(errorMessage(stringifyError(x)))))
      }
    } catch (e) {
      console.error(e)
      const message = e instanceof Error ? e.message : String(e)
      dispatch(addMessage(errorMessage(message)))
    }
  }

  const openEditedCsvFlow = () => {
    if (props.samplePools === 'LOADING') return

    const table = buildSamplePoolCsvTable(props.samplePools)

    openModal(
      'editCsvModal',
      {
        contextLabel: `Order ${props.order.id}`,
        csvMode,
        initialColumns: table.columns,
        initialRows: table.rows
      },
      (editResult) => {
        if (!editResult) return

        openModal(
          'parseCSV',
          {
            rawCSVData: editResult.rows,
            fromGrid: true,
            mode: csvMode,
            order: props.order
          },
          (parseResult) => {
            if (!parseResult) return
            csvOk(parseResult)
          }
        )
      }
    )
  }

  return (
    <div className="flex gap-x-2 sm:max-md:flex-col sm:max-md:gap-2 sm:max-md:py-2">
      {(props.order.lab == user?.org?.id || props.order.customer == user?.org?.id) && (
        <>
          <TooltipTrigger
            tooltip={
              csvMode === 'UPDATE_METADATA'
                ? 'Update Sample Metadata, such as date taken, location, ... by uploading a csv and matching value columns.'
                : csvMode === 'PREPARE_METADATA'
                  ? 'Prepare Sample Metadata, such as date taken, location, ... by uploading a csv and matching value columns. Prepared metadata is merged into actual samples uploaded by the lab by matching via customer sample id'
                  : csvMode === 'CREATE_SAMPLES'
                    ? 'Prepare Sample Metadata (date taken, location) and analysis data (primers). Primers cannot be changed after submission, other metadata may be changed. Customer prepared metadata will be merged by matching via customer sample names. Data must be uploaded in csv format.'
                    : 'Unknown mode'
            }
          >
            <label
              role="button"
              className={`sm:max-md:btn-lg sm:max-md:w-full btn btn-sm ${canEditCsv ? 'btn-actionable' : 'btn-disabled'}`}
              htmlFor={`uploadPrimerInfoBtn${props.order.id}`}
            >
              <input
                data-testid="upload-csv"
                type="file"
                hidden
                id={`uploadPrimerInfoBtn${props.order.id}`}
                accept=".csv"
                disabled={!canEditCsv}
                onChange={async (event) => {
                  trackOrderAction('upload_csv', props.order)
                  await handleCSVUpload(event, confirm, dispatch, (data) =>
                    openModal(
                      'parseCSV',
                      {
                        rawCSVData: data.data as Record<string, string>[],
                        mode: csvMode,
                        order: props.order
                      },
                      (result) => {
                        if (!result) return
                        csvOk(result)
                      }
                    )
                  )
                }}
              />
              <DocumentArrowUpIcon className="w-4 h-4" />
              {csvMode === 'UPDATE_METADATA'
                ? 'Update Sample Metadata'
                : csvMode === 'PREPARE_METADATA'
                  ? 'Prepare Sample Metadata'
                  : csvMode === 'CREATE_SAMPLES'
                    ? 'Upload samplesheet.csv'
                    : 'Unknown mode'}
            </label>
          </TooltipTrigger>

          {canEditCsvInGrid && (
            <TooltipTrigger tooltip="Open the generated pool CSV in an editable grid. When you submit, the edited data is passed into the same processing flow as a CSV upload.">
              <button
                type="button"
                data-testid="editCSV"
                disabled={!hasSamples}
                className="btn btn-sm btn-outline btn-secondary sm:max-md:w-full sm:max-md:btn-lg"
                onClick={() => {
                  trackOrderAction('edit_data_beta', props.order)
                  openEditedCsvFlow()
                }}
              >
                <PencilSquareIcon className="w-4 h-4 mr-2" />
                Edit Data
              </button>
            </TooltipTrigger>
          )}

          <TooltipTrigger tooltip="Download an example sample sheet for this order. Contains all already set information, the specify sample ids and all possible metadata headers.">
            <button
              data-testid="downloadCSV"
              disabled={!hasSamples}
              className="btn btn-sm btn-outline btn-primary sm:max-md:w-full sm:max-md:btn-lg"
              onClick={() => {
                trackOrderAction('download_pool_csv', props.order)
                downloadSamplePoolCSV(props.order, props.samplePools !== 'LOADING' ? props.samplePools : [])
              }}
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Download Pool CSV
            </button>
          </TooltipTrigger>
        </>
      )}
    </div>
  )
}
